import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import { Link } from "react-router-dom";
import Avatar from "@/components/common/Avatar";
import {
  fetchUsersByAny,
  selectFetchedUsers,
  selectUserAdminLoading,
} from "@/store/redux/slices/user_admin/user_admin";
import { getOrganizationId } from "@/utils/token_functions";

const AccountMembers = () => {
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const dispatch = useDispatch();

  // Use the selectors instead of directly accessing state properties
  const users = useSelector(selectFetchedUsers);
  const isLoading = useSelector(selectUserAdminLoading);

  const fetchUserData = useCallback(async () => {
    const organizationID = getOrganizationId();

    try {
      await dispatch(
        fetchUsersByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setHasAttemptedFetch(true);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Get first 8 members for preview display
  const previewMembers = users?.slice(0, 8);

  const handleViewAll = () => {
    setIsViewAllOpen(true);
  };

  // Placeholder image for users without profile image
  const getProfileImage = (user) => {
    return (
      user.profileImage ||
      ''
    );
  };

  // Get display name for user - only first name
  const getDisplayName = (user) => {
    if (user.fullname) {
      // Extract first name from full name
      return user.fullname.split(" ")[0];
    } else if (user.username) {
      return user.username;
    } else if (user.email) {
      return user.email.split("@")[0];
    } else {
      return "Unknown User";
    }
  };

  const renderMembers = () => {
    if (isLoading && !hasAttemptedFetch) {
      return <div className="text-center py-4">Loading members...</div>;
    }

    if (!isLoading && (!Array.isArray(users) || users.length === 0)) {
      return (
        <div className="text-center py-4">
          No team members found. Add members to get started.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-4">
        {previewMembers.map((member) => (
          <div key={member.id} className="flex flex-col items-center">
            <Link to={`/dashboard/users/${member._id}`}>
              <Avatar
                src={getProfileImage(member)}
                alt={getDisplayName(member)}
                size={48}
                className="cursor-pointer"
              />
            </Link>
            <p className="my-2 text-sm text-black">{getDisplayName(member)}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Team Members</h2>
        <button
          className="text-sm font-medium text-primary"
          onClick={handleViewAll}
          disabled={!users || users.length === 0}>
          View all
        </button>
      </div>

      <div className="w-full">{renderMembers()}</div>

      {/* View All Members Modal */}
      <Modal
        open={isViewAllOpen}
        onCancel={() => setIsViewAllOpen(false)}
        footer={null}
        centered
        width={800}
        title="All Account Members">
        <div className="max-h-[500px] overflow-y-auto p-4">
          {isLoading && !hasAttemptedFetch ? (
            <div className="text-center py-4">Loading users...</div>
          ) : !users || users.length === 0 ? (
            <div className="text-center py-4">
              No team members found. Add members to get started.
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-4">
              {users.map((member, index) => (
                <div
                  key={member?.id || index}
                  className="flex flex-col items-center">
                  <Link to={`/dashboard/users/${member?._id}`}>
                    <Avatar
                      src={getProfileImage(member)}
                      alt={getDisplayName(member)}
                      size={64}
                      className="cursor-pointer"
                    />
                  </Link>
                  <p className="mt-2 text-small font-medium text-center text-gray-800">
                    {getDisplayName(member)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AccountMembers;
